import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import { MODAL_TYPE } from '../../../models';
import { ResourcesService } from '../../../services';
import { MessageImageHolder } from '../multimedia-message-bubble/components/MessageImageHolder';
import { MessageVideoHolder } from '../multimedia-message-bubble/components/MessageVideoHolder';
import DocumentTemplateHeader from './DocumentTemplateHeader';
import LocationTemplateHeader from './LocationTemplateHeader';

const MultimediaContentTemplate = ({
  multimediaId,
  multimediaCode,
  multimediaFileName,
  resourceURL,
  onMultimediaClick,
  image,
  video,
  document,
  location,
  locationParameters,
  previewTemplate,
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const [resourceSource, setResourceSource] = useState();
  const [isDownloadError, setIsdownloadError] = useState(false);
  const [ref, inView] = useInView({
    /* Optional options */
    threshold: 0.25,
  });
  const isMounted = useRef(true);
  const SCROLL_DELAY_TIME = 100;

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const getMultimedia = async () => {
    setIsFetching(true);
    setIsdownloadError(false);
    try {
      let idType = false;
      if (multimediaId !== null && multimediaId !== undefined) {
        idType = 'multimediaId';
      } else if (multimediaCode !== null && multimediaCode !== undefined) {
        idType = 'multimediaCode';
      }
      const url = idType
        ? await ResourcesService.getResource(
            idType === 'multimediaId' ? multimediaId : multimediaCode,
            idType === 'multimediaCode'
          )
        : resourceURL;
      setResourceSource(url);
    } catch (error) {
      setIsdownloadError(true);
    } finally {
      setIsFetching(false);
    }
  };

  const fetchMultimedia = useCallback(getMultimedia, []);
  useEffect(() => {
    let timer;
    if (inView && !resourceSource && isMounted.current) {
      timer = setTimeout(() => {
        fetchMultimedia();
      }, SCROLL_DELAY_TIME);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [fetchMultimedia, inView, resourceSource]);

  const handleClick = () => {
    const action = image ? MODAL_TYPE.PREVIEW.IMAGE : MODAL_TYPE.PREVIEW.VIDEO;
    onMultimediaClick(action, resourceSource);
  };

  return (
    <div ref={ref}>
      {image && (
        <MessageImageHolder
          handleClick={handleClick}
          imageSource={resourceSource}
          isDownloadError={isDownloadError}
          isFetching={isFetching}
          previewTemplate={previewTemplate}
        />
      )}
      {video && (
        <MessageVideoHolder
          handleClick={handleClick}
          isDownloadError={isDownloadError}
          isFetching={isFetching}
          previewTemplate={previewTemplate}
          videoSource={resourceSource}
        />
      )}
      {document && (
        <DocumentTemplateHeader
          docSource={resourceSource}
          isDownloadError={isDownloadError}
          isFetching={isFetching}
          multimediaFileName={multimediaFileName}
          previewTemplate={previewTemplate}
          viewRef={ref}
        />
      )}
      {location && (
        <LocationTemplateHeader
          isDownloadError={isDownloadError}
          isFetching={isFetching}
          locationParameters={locationParameters}
          previewTemplate={previewTemplate}
          viewRef={ref}
        />
      )}
    </div>
  );
};
MultimediaContentTemplate.propTypes = {
  document: PropTypes.bool,
  image: PropTypes.bool,
  location: PropTypes.bool,
  locationParameters: PropTypes.object,
  multimediaCode: PropTypes.number,
  multimediaFileName: PropTypes.string,
  multimediaId: PropTypes.number,
  onMultimediaClick: PropTypes.func,
  previewTemplate: PropTypes.bool,
  resourceURL: PropTypes.string,
  video: PropTypes.bool,
};
export default MultimediaContentTemplate;
